import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MaterialModule } from 'src/app/material.module';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MatTableDataSource } from '@angular/material/table';
import { IpdService } from '../../ipd.service';
import { IActiveIPDPatient } from '../iipd-management';
import { debounceTime, distinctUntilChanged, Subject, Observable, map, startWith } from 'rxjs';

@Component({
  selector: 'app-ipd-active-list',
  standalone: true,
  imports: [CommonModule, MaterialModule, FormsModule, ReactiveFormsModule, TranslateModule, TablerIconsModule],
  templateUrl: './ipd-active-list.component.html',
  styleUrls: ['./ipd-active-list.component.scss']
})
export class IpdActiveListComponent implements OnInit {
  displayedColumns = ['ipdNumber', 'patientName', 'wardRoom', 'doctor', 'admissionDate', 'days', 'diagnosis'];
  dataSource = new MatTableDataSource<IActiveIPDPatient>();
  isLoading = false;

  wardFilter: number | null = null;
  doctorFilter: number[] = [];
  searchText = '';

  wardsList: { id: number; name: string }[] = [];
  doctorsList: { id: number; name: string }[] = [];

  doctorControls = new FormControl('');
  filteredDoctorList$: Observable<{ id: number; name: string }[]>;

  searchSubject = new Subject<string>();

  constructor(
    private ipdService: IpdService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.wardsList = this.route.parent?.snapshot.data['ddlward'] || this.route.snapshot.data['ddlward'] || [];
    this.doctorsList = this.route.parent?.snapshot.data['ddlDoctor'] || this.route.snapshot.data['ddlDoctor'] || [];

    this.filteredDoctorList$ = this.doctorControls.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      map((value) => {
        const val = typeof value === 'string' ? value : '';
        return this._filterDoctorContent(val);
      })
    );

    this.loadActivePatients();

    this.searchSubject
      .pipe(
        debounceTime(800),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.loadActivePatients();
      });
  }

  loadActivePatients(): void {
    this.isLoading = true;
    const request = {
      wardId: this.wardFilter,
      doctorIds: this.doctorFilter.length > 0 ? this.doctorFilter : null,
      searchText: this.searchText?.trim() || null
    };
    this.ipdService.getActiveIPDPatients(request).subscribe({
      next: (res) => {
        if (res.isSuccess) {
          this.dataSource.data = res.result;
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load active IPD patients', err);
        this.isLoading = false;
      }
    });
  }

  onSearch(): void {
    this.loadActivePatients();
  }

  private _filterDoctorContent(value: string): { id: number; name: string }[] {
    const filterValue = value.toLowerCase();
    return this.doctorsList.filter(option =>
      option.name.toLowerCase().includes(filterValue)
    );
  }

  onDoctorToggle(doc: { id: number; name: string }): void {
    const index = this.doctorFilter.indexOf(doc.id);
    if (index > -1) {
      this.doctorFilter.splice(index, 1);
    } else {
      this.doctorFilter.push(doc.id);
    }

    this.doctorControls.setValue(
      this.doctorsList.filter(d => this.doctorFilter.includes(d.id)).map(d => d.name).join(', '),
      { emitEvent: false }
    );
  }

  isDoctorSelected(id: number): boolean {
    return this.doctorFilter.includes(id);
  }

  clearFilters(): void {
    this.wardFilter = null;
    this.doctorFilter = [];
    this.doctorControls.setValue('', { emitEvent: false });
    this.searchText = '';
    this.loadActivePatients();
  }

  viewPatient(row: IActiveIPDPatient): void {
    this.router.navigate(['/ipd/admit', row.ipdId, 'patient', row.patientId]);
  }

  onSearchChange(value: string): void {
    this.searchText = value;
    this.searchSubject.next(value);
  }

}
